import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import { Icon24ChevronLeft, Icon20Check } from '@vkontakte/icons';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Text from '@/components/Text';
import Button, { ButtonType } from '@/components/Button';
import ListItem from '@/common/ListItem';
import Section from '@/common/Section';
import Heading from '@/common/Heading';
import AssetImage from '@/components/AssetImage';
import useHasActiveSubscription from '@/hooks/useHasActiveSubscription';

import useAssetInfo from '@/hooks/useAssetInfo';

import { INDUSTRY_CHAPTERS, INDUSTRY_GOAL_CHAPTER_ORDER } from '../BrowseConcept/industries';
import { generateCourseSkeleton, callPerplexityAPI, generateChapter } from '@/utils/openai';
import { insertGeneratedCourseSnapshot, updateGeneratedCourseSnapshot, fetchGeneratedCourseSnapshotById, StoredGeneratedCoursePayload } from '@/utils/supabase';
import { firebaseAuth } from '@/lib/Firebase';

interface ChoosePreferencesProps {
  stockName: string;
  ticker: string;
  isIndustry: boolean;
  onBack: () => void;
  onBuild: (params: { id: string; payload: any }) => void;
  userId?: string;
  onRequireAuth?: () => Promise<boolean>;
}

const GOAL_CHAPTER_ORDER: { [key: string]: string[] } = {
  quick: [
    'Understand the business',
    'Market/TAM/competition',
    'Basic financials',
    'Risk awareness',
  ],
  solid: [
    'Understand the business',
    'Market/TAM/competition',
    'Basic financials',
    'Risk awareness',
    'Moat / competitive advantage',
    'Management quality',
    'Financial depth',
    'Catalysts',
  ],
  deep: [
    'Understand the business',
    'Market/TAM/competition',
    'Basic financials',
    'Risk awareness',
    'Moat / competitive advantage',
    'Management quality',
    'Financial depth',
    'Catalysts',
    'Valuation',
    'Price action / technicals',
  ],
};

const stockChapters = [
  {
    value: 'understand-business',
    label: 'Understand the business',
    description: 'What does the company do and how does it make money?',
  },
  {
    value: 'market-tam-competition',
    label: 'Market/TAM/competition',
    description: 'Total addressable market size, market position, and competitive landscape.',
  },
  {
    value: 'basic-financials',
    label: 'Basic financials',
    description: 'Key financial metrics and overall financial health.',
  },
  {
    value: 'risk-awareness',
    label: 'Risk awareness',
    description: 'Key risks and potential challenges facing the company.',
  },
  {
    value: 'moat-competitive-advantage',
    label: 'Moat / competitive advantage',
    description: 'What makes this company defensible and hard to compete against?',
  },
  {
    value: 'management-quality',
    label: 'Management quality',
    description: 'Leadership team, track record, and strategic vision.',
  },
  {
    value: 'financial-depth',
    label: 'Financial depth',
    description: 'Deep dive into financial statements, metrics, and trends.',
  },
  {
    value: 'catalysts',
    label: 'Catalysts',
    description: 'Upcoming events or changes that could drive stock movement.',
  },
  {
    value: 'valuation',
    label: 'Valuation',
    description: 'Is the stock fairly priced, cheap, or expensive?',
  },
  {
    value: 'price-action-technicals',
    label: 'Price action / technicals',
    description: 'Chart patterns, technical indicators, and trading signals.',
  },
];

const goalOptions = [
  { 
    value: 'quick', 
    label: 'Quick Overview',
    description: 'Get the essential info fast. Perfect for quick decisions.'
  },
  { 
    value: 'solid', 
    label: 'Solid Understanding',
    description: 'Comprehensive coverage of key business fundamentals.'
  },
  { 
    value: 'deep', 
    label: 'Deep Dive & Mastery',
    description: 'Complete analysis with advanced insights and case studies.'
  },
];

const levelOptions = [
  { 
    value: 'beginner', 
    title: 'Beginner',
    description: 'New to investing? We&apos;ll explain everything in simple terms.'
  },
  { 
    value: 'intermediate', 
    title: 'Intermediate',
    description: 'Know the basics? We&apos;ll dive deeper into analysis and strategy.'
  },
  { 
    value: 'professional', 
    title: 'Professional',
    description: 'Advanced investor? Get technical analysis and expert insights.'
  },
];

export default function ChoosePreferences({ stockName, ticker, isIndustry, onBack, onBuild, userId, onRequireAuth }: ChoosePreferencesProps) {
  const router = useRouter();
  const [selectedGoal, setSelectedGoal] = useState<string | null>('quick');
  const [selectedLevel, setSelectedLevel] = useState<string | null>('beginner');
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [showCustomizeTopics, setShowCustomizeTopics] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const { hasActiveSubscription, isLoading: isLoadingSubscription } = useHasActiveSubscription(false, !!userId);
  const { data: assetInfo } = useAssetInfo(isIndustry ? '' : ticker);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const description = assetInfo?.description;

  const allChapters = isIndustry ? INDUSTRY_CHAPTERS : stockChapters;

  // Get ordered chapters based on goal
  const getOrderedChapters = () => {
    if (!selectedGoal) return [];
    
    const chapterOrder = isIndustry ? INDUSTRY_GOAL_CHAPTER_ORDER : GOAL_CHAPTER_ORDER;
    const orderedTitles = chapterOrder[selectedGoal] || [];
    const orderedChapters: typeof allChapters = [];
    
    orderedTitles.forEach((title) => {
      const chapter = allChapters.find((ch) => ch.label === title);
      if (chapter) {
        orderedChapters.push(chapter);
      }
    });
    
    return orderedChapters;
  };

  // Auto-select chapters based on goal
  useEffect(() => {
    if (!selectedGoal) {
      setSelectedTopics([]);
      return;
    }

    const chapterOrder = isIndustry ? INDUSTRY_GOAL_CHAPTER_ORDER : GOAL_CHAPTER_ORDER;
    const orderedTitles = chapterOrder[selectedGoal] || [];
    const orderedTopics = allChapters
      .filter((ch) => orderedTitles.includes(ch.label))
      .map((ch) => ch.value);
    
    setSelectedTopics(orderedTopics);
  }, [selectedGoal, isIndustry, allChapters]);

  const onBuildCourse = async () => {
    if (!selectedGoal || !selectedLevel || selectedTopics.length === 0) return;

    // If user is not authenticated, require login before building
    if (onRequireAuth && !userId) {
      const authenticated = await onRequireAuth();
      if (!authenticated) return;
    }

    if (isLoadingSubscription) {
      return;
    }

    if (!hasActiveSubscription) {
      void router.push('/subscription');
      return;
    }

    // Get the current user ID (may have just authenticated via modal)
    const currentUserId = userId || firebaseAuth.currentUser?.uid;

    setIsBuilding(true);

    try {
      const orderedChapters = getOrderedChapters();
      const filteredChapters = orderedChapters.filter((ch) => selectedTopics.includes(ch.value));
      const orderedTopicKeys = filteredChapters.map((ch) => ch.value);
      
      const selectedTopicsObj: { [key: string]: string[] } = {
        'Course Chapters': orderedTopicKeys,
      };

      console.log('📝 Generating course skeleton...');
      const skeleton = generateCourseSkeleton({
        stockName,
        goal: selectedGoal,
        selectedTopics: selectedTopicsObj,
        industryChapters: isIndustry ? INDUSTRY_CHAPTERS.map((ch) => ({ value: ch.value, label: ch.label })) : undefined,
      });

      const skeletonCourse = {
        title: skeleton.title,
        description: `Custom course for ${stockName}`,
        chapters: skeleton.chapters.map((chapter) => ({
          title: chapter.title,
          lessons: [],
        })),
      };

      const topicKeyMapping: { [chapterTitle: string]: string } = {};
      skeleton.chapters.forEach((ch) => {
        topicKeyMapping[ch.title] = ch.topicKey;
      });

      console.log('💾 Saving skeleton to Supabase...');
      const now = Date.now();
      const payload: StoredGeneratedCoursePayload = {
        outline: skeletonCourse,
        generatedLessons: {},
        lessonCompletion: {},
        metadata: {
          stockName,
          ticker,
          goal: selectedGoal,
          level: selectedLevel,
          selectedTopics: selectedTopicsObj,
          topicKeyMapping,
          generatedAt: new Date().toISOString(),
          chapterTriggerTimestamps: Object.fromEntries(
            skeleton.chapters.map((_, idx) => [idx, now])
          ),
          isIndustry: isIndustry || false,
        },
      };

      const record = await insertGeneratedCourseSnapshot({
        courseTitle: skeletonCourse.title,
        coursePayload: payload,
        notes: `goal=${selectedGoal} | level=${selectedLevel}`,
        ticker: ticker,
        userId: currentUserId,
      });

      if (!record?.id) {
        throw new Error('Failed to save course to database');
      }

      console.log('✅ Skeleton saved with ID:', record.id);

      const totalChapters = skeleton.chapters.length;

      if (totalChapters > 0) {
        const firstChapter = skeleton.chapters[0];
        console.log(`📱 Starting client-side generation for chapter 0: ${firstChapter.title}`);

        (async () => {
          try {
            console.log(`  🔮 Fetching Perplexity data for ${firstChapter.topicKey}...`);
            const perplexityData = await callPerplexityAPI({
              stockName,
              ticker,
              topicKey: firstChapter.topicKey,
            });

            console.log(`  🤖 Generating chapter 0 content...`);
            const chapterResult = await generateChapter({
              stockName,
              ticker,
              chapterTitle: firstChapter.title,
              level: selectedLevel!,
              goal: selectedGoal!,
              perplexityData,
            });

            const latestSnapshot = await fetchGeneratedCourseSnapshotById(record.id, currentUserId);
            if (!latestSnapshot?.course_payload) return;

            const latestPayload = latestSnapshot.course_payload as StoredGeneratedCoursePayload;
            const latestOutline = latestPayload.outline;

            const chapterLessons = chapterResult.lessons.map((lesson, lessonIndex) => ({
              title: lesson.lessonTitle,
              description: lesson.lessonDescription || '',
              hookCta: `Discover ${lesson.lessonTitle}`,
              metadata: {
                chapter: firstChapter.title,
                order: lessonIndex,
              },
              blocks: lesson.blocks,
            }));

            const updatedChapters = [...latestOutline.chapters];
            updatedChapters[0] = {
              ...updatedChapters[0],
              lessons: chapterLessons,
            };

            const newGeneratedLessons: { [key: string]: any } = {
              ...latestPayload.generatedLessons,
            };
            chapterResult.lessons.forEach((lesson, lessonIndex) => {
              const key = `0-${lessonIndex}`;
              newGeneratedLessons[key] = lesson.blocks;
            });

            const updatedPayload: StoredGeneratedCoursePayload = {
              ...latestPayload,
              outline: {
                ...latestOutline,
                chapters: updatedChapters,
              },
              generatedLessons: newGeneratedLessons,
            };

            console.log(`  💾 Saving chapter 0 to Supabase...`);
            await updateGeneratedCourseSnapshot({
              id: record.id,
              coursePayload: updatedPayload,
            });
            console.log(`  ✅ Chapter 0 saved successfully!`);

          } catch (error) {
            console.error(`  ❌ Client-side chapter 0 generation failed:`, error);
          }
        })();
      }

      if (totalChapters > 1) {
        const edgeFunctionUrl = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/generate_remaining_chapters_web`;

        for (let chapterIndex = 1; chapterIndex < totalChapters; chapterIndex++) {
          const delay = (chapterIndex - 1) * 100;
          
          setTimeout(async () => {
            try {
              console.log(`🚀 Triggering edge function for chapter ${chapterIndex}...`);
              const response = await fetch(edgeFunctionUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  apikey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
                  Authorization: `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`,
                },
                body: JSON.stringify({
                  snapshotId: record.id,
                  chapterIndex,
                }),
              });
              
              if (!response.ok) {
                const errorText = await response.text();
                console.error(`❌ Chapter ${chapterIndex} edge function failed:`, errorText);
              } else {
                console.log(`✅ Chapter ${chapterIndex} edge function triggered!`);
              }
            } catch (err) {
              console.error(`❌ Failed to trigger edge function for chapter ${chapterIndex}:`, err);
            }
          }, delay);
        }
      }

      onBuild({ id: record.id, payload });

    } catch (error) {
      console.error('❌ Error building course:', error);
      alert('Failed to create course. Please try again.');
    } finally {
      setIsBuilding(false);
    }
  };

  const handleTopicToggle = (value: string) => {
    setSelectedTopics(prev => 
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  const activeLevel = levelOptions.find(opt => opt.value === selectedLevel);

  return (
    <>
    <div className={clsx(
      "flex flex-col gap-4 pb-24 transition-all duration-700 ease-out",
      isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
    )}>
      <div className="flex justify-between items-center gap-4 py-2 mb-4 px-0">
        <Button 
          type={ButtonType.IconButton} 
          className='bg-btn-primary flex-none !w-8 !h-8 md:!w-10 md:!h-10 !p-0 flex items-center justify-center' 
          onClick={onBack}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3 text-white flex-shrink-0" />
        </Button>
        <h1 className="flex items-center gap-2 font-sans font-normal text-base md:text-lg capitalize flex-grow">
          {isIndustry ? 'Generate industry course' : 'Generate course'}
        </h1>
      </div>

      {/* Selected Asset Header */}
      <Section className="p-6 bg-gradient-blue-purple overflow-hidden relative rounded-3xl hover:brightness-110 transition-all duration-500 cursor-default group">
        <div className="flex flex-col gap-4 relative z-10">
          <div className="flex items-center gap-4">
            {!isIndustry && ticker && (
              <AssetImage alt={ticker} src="" width={64} height={64} className="w-16 h-16 rounded-2xl bg-white/10 group-hover:scale-110 transition-transform duration-500" />
            )}
            <div className="group-hover:translate-x-1 transition-transform duration-300">
              <h2 className="text-lg md:text-2xl text-white">{stockName}</h2>
              {ticker && !isIndustry && <Text color="secondary" size="md" className="text-white/70 font-normal text-sm md:text-md">{ticker}</Text>}
            </div>
          </div>
          {description && (
            <Text color="secondary" size="sm" className="text-white/60 line-clamp-3 leading-relaxed">
              {description}
            </Text>
          )}
        </div>
        {/* Decorative background element */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16 blur-2xl group-hover:bg-white/10 transition-colors duration-500" />
      </Section>

      {/* Level Selector */}
      <Section title="Your Level" titleInside className="px-0 md:px-6">
        <div className="px-4 md:px-0">
          <div className="flex bg-white/5 p-1 rounded-2xl mb-4">
            {levelOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSelectedLevel(opt.value)}
                className={clsx(
                  "flex-1 py-3 text-sm rounded-xl transition-all duration-300",
                  selectedLevel === opt.value 
                    ? "bg-white/10 text-white shadow-lg scale-[1.02]" 
                    : "text-secondary hover:text-white hover:bg-white/5"
                )}
              >
                {opt.title}
              </button>
            ))}
          </div>
          {activeLevel && (
            <div className="animate-in fade-in slide-in-from-left-2 duration-300">
            <Text color="secondary" size="sm" className="px-2">
              {activeLevel.description}
            </Text>
            </div>
          )}
        </div>
      </Section>

      {/* Goal Selector */}
      <Section title="Your Goal" titleInside className="px-0 md:px-6">
        <div className="divide-y divide-line-primary/30 overflow-hidden rounded-xl bg-darkglass mx-4 md:mx-0">
          {goalOptions.map((opt, index) => (
            <ListItem 
              key={opt.value} 
              onClick={() => setSelectedGoal(opt.value)}
              className="cursor-pointer px-3 md:px-4 transition-all duration-300 hover:bg-white/5 group w-full"
            >
              <ListItem.Left className="flex-1 min-w-0 w-full">
                <div className={clsx(
                  "h-5 w-5 rounded-full border-2 flex items-center justify-center transition-all duration-300 mr-3 flex-none flex-shrink-0",
                  selectedGoal === opt.value ? "border-success bg-success/20 scale-110" : "border-line-primary group-hover:border-white/30"
                )}>
                  {selectedGoal === opt.value && <div className="h-2.5 w-2.5 rounded-full bg-success animate-in zoom-in duration-300" />}
                </div>
                <div className="flex flex-col min-w-0 overflow-hidden flex-1 group-hover:translate-x-1 transition-transform duration-300">
                  <Text size="md" color="primary" className={clsx("truncate text-sm md:text-md transition-colors", selectedGoal === opt.value ? "text-success" : "")}>{opt.label}</Text>
                  <Text size="sm" color="secondary" className="opacity-70 line-clamp-1 md:line-clamp-none text-xs md:text-sm">{opt.description}</Text>
                </div>
              </ListItem.Left>
            </ListItem>
          ))}
        </div>
      </Section>

      {/* Customize Topics */}
      <div className="px-4">
        <button 
          onClick={() => setShowCustomizeTopics(!showCustomizeTopics)}
          className="text-blue hover:text-blue-400 transition-colors text-sm md:text-base flex items-center gap-1 group"
        >
          <span className="group-hover:underline underline-offset-4 decoration-blue/30">{showCustomizeTopics ? 'Hide topics' : 'Customize topics'}</span>
          <div className={clsx(
            "transition-transform duration-300",
            showCustomizeTopics ? "rotate-180" : ""
          )}>
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </button>
      </div>

      {showCustomizeTopics && (
        <div className="animate-in fade-in slide-in-from-top-4 duration-500">
          <Section title="Customize Course Chapters" titleInside className="px-0 md:px-6">
            <div className="divide-y divide-line-primary/30 overflow-hidden rounded-xl bg-darkglass mx-4 md:mx-0">
              {allChapters.map((chapter, index) => (
                <ListItem 
                  key={chapter.value} 
                  onClick={() => handleTopicToggle(chapter.value)}
                  className="cursor-pointer px-3 md:px-4 transition-all duration-200 hover:bg-white/5 group w-full"
                >
                  <ListItem.Left className="flex-1 min-w-0 w-full">
                    <div className={clsx(
                      "h-5 w-5 rounded border-2 flex items-center justify-center transition-all duration-300 mr-3 flex-none flex-shrink-0",
                      selectedTopics.includes(chapter.value) ? "border-success bg-success" : "border-line-primary group-hover:border-white/30"
                    )}>
                      {selectedTopics.includes(chapter.value) && (
                        <Icon20Check className="text-white w-3 h-3 animate-in zoom-in duration-200" />
                      )}
                    </div>
                    <div className="flex flex-col min-w-0 overflow-hidden flex-1 group-hover:translate-x-1 transition-transform duration-200">
                      <Text size="md" color="primary" className="truncate text-sm md:text-md">{chapter.label}</Text>
                      <Text size="sm" color="secondary" className="opacity-70 line-clamp-1 md:line-clamp-none text-xs md:text-sm">{chapter.description}</Text>
                    </div>
                  </ListItem.Left>
                </ListItem>
              ))}
            </div>
          </Section>
        </div>
      )}
    </div>
    <div className="fixed bottom-0 left-0 md:left-[144px] xl:left-[304px] right-0 pb-6 md:pb-8 z-50 pointer-events-none">
      <div className="flex justify-start px-4 md:px-0">
        <Button
          type={ButtonType.Primary}
          size="md"
          className={clsx(
            "pointer-events-auto w-auto md:w-auto whitespace-nowrap",
            isBuilding 
              ? "!bg-[#1658FF]/50 !text-white !border-0 cursor-not-allowed opacity-50"
              : ""
          )}
          onClick={onBuildCourse}
          disabled={isBuilding || isLoadingSubscription}
        >
          {isBuilding ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Creating Course...</span>
            </div>
          ) : 'Build Course'}
        </Button>
      </div>
    </div>
    </>
  );
}
